import { commandMatches } from "../tasks/productivity.ts";
import { escapeHtml } from "../../ui/text.ts";

export interface CommandPaletteItem {
  id: string;
  label: string;
  hint: string;
  group?: "Actions" | "Settings" | "Spaces" | "Collections" | "Views" | "Templates" | "Recent" | "Tasks";
  description?: string;
  keywords?: string;
  run(): void | Promise<void>;
}

export interface CommandPaletteCapabilities {
  icon(name: string): string;
}

export function matchingCommands(commands: CommandPaletteItem[], query: string): CommandPaletteItem[] {
  return commands.filter(command => commandMatches(`${command.label} ${command.description || ""} ${command.keywords || ""}`, query));
}

function commandIcon(id: string): string {
  return id.startsWith("task-") ? "check" : id.startsWith("template") ? "template" : "spark";
}

export function openCommandPalette(commands: CommandPaletteItem[], capabilities: CommandPaletteCapabilities): void {
  if (document.querySelector(".command-dialog")) return;
  const dialog = document.createElement("dialog");
  dialog.className = "name-dialog command-dialog";
  dialog.setAttribute("aria-label", "Search and commands");
  const groups: NonNullable<CommandPaletteItem["group"]>[] = ["Actions", "Settings", "Spaces", "Collections", "Views", "Templates", "Recent", "Tasks"];
  const ordered = groups.flatMap(group => commands.filter(command => (command.group || "Actions") === group));
  let visible = ordered;
  let selected = 0;
  const run = async (id: string) => {
    const command = commands.find(item => item.id === id);
    if (!command) return;
    dialog.close();
    await command.run();
  };
  const renderResults = () => {
    const list = dialog.querySelector<HTMLElement>(".command-results")!;
    list.innerHTML = groups.map(group => {
      const entries = visible.filter(command => (command.group || "Actions") === group);
      return entries.length ? `<div class="command-group" role="group" aria-label="${group}"><h3 aria-hidden="true">${group}</h3>${entries.map(command => {
        const index = visible.indexOf(command);
        return `<button type="button" role="option" tabindex="-1" id="command-option-${index}" aria-selected="${index === selected}" data-command="${escapeHtml(command.id)}" class="${index === selected ? "is-selected" : ""}">${capabilities.icon(commandIcon(command.id))}<span class="command-result-copy"><span>${escapeHtml(command.label)}</span>${command.description ? `<small>${escapeHtml(command.description)}</small>` : ""}</span>${command.hint ? `<kbd>${escapeHtml(command.hint)}</kbd>` : ""}</button>`;
      }).join("")}</div>` : "";
    }).join("");
    dialog.querySelector<HTMLElement>(".command-empty")!.hidden = !!visible.length;
    const count = dialog.querySelector<HTMLElement>(".command-count")!;
    const countText = `${visible.length} ${visible.length === 1 ? "result" : "results"}`;
    if (count.textContent !== countText) count.textContent = countText;
    const input = dialog.querySelector<HTMLInputElement>("input")!;
    if (visible[selected]) input.setAttribute("aria-activedescendant", `command-option-${selected}`);
    else input.removeAttribute("aria-activedescendant");
    list.querySelectorAll<HTMLButtonElement>("[data-command]").forEach(button => button.addEventListener("click", () => void run(button.dataset.command!)));
    list.querySelector(".is-selected")?.scrollIntoView({ block: "nearest" });
  };
  dialog.innerHTML = `<div class="command-scope"><strong>Search and commands</strong><span id="command-scope">All spaces available on this device</span></div><div class="command-search">${capabilities.icon("search")}<input type="search" role="combobox" aria-autocomplete="list" aria-expanded="true" aria-controls="command-results" aria-describedby="command-scope" placeholder="Find tasks, collections, actions or settings…" aria-label="Command search"><button type="button" class="icon-button" data-command-close aria-label="Close search">${capabilities.icon("close")}</button></div><div class="command-results" id="command-results" role="listbox" aria-label="Search results"></div><p class="command-empty" hidden>No matches. Try a task name or a feature such as “sharing” or “date format”.</p><span class="command-count sr-only" role="status"></span><footer><span>↑↓ Navigate</span><span>↵ Select</span><span>Esc Close</span></footer>`;
  dialog.querySelector("[data-command-close]")!.addEventListener("click", () => dialog.close());
  const input = dialog.querySelector<HTMLInputElement>("input")!;
  input.addEventListener("input", () => { visible = matchingCommands(ordered, input.value); selected = 0; renderResults(); });
  input.addEventListener("keydown", event => {
    if (event.isComposing) return;
    if (event.key === "ArrowDown") { event.preventDefault(); selected = Math.max(0, Math.min(visible.length - 1, selected + 1)); renderResults(); }
    if (event.key === "ArrowUp") { event.preventDefault(); selected = Math.max(0, selected - 1); renderResults(); }
    if (event.key === "Enter" && visible[selected]) { event.preventDefault(); void run(visible[selected].id); }
  });
  renderResults();
  document.body.append(dialog);
  dialog.addEventListener("close", () => dialog.remove());
  dialog.showModal();
  input.focus();
}
